import React, { FunctionComponent, useEffect } from 'react';
import { pathOr, pick, without } from 'ramda';
import { withRouter, match } from 'react-router';
import withStyles from 'react-jss';
import { compose } from 'recompose';
import { Location, History } from 'history';
import queryString from 'query-string';
import { Container, Header, Loader } from 'semantic-ui-react';
import {
  ExecutionResult,
  MutationFunctionOptions,
  OperationVariables,
  useQuery,
} from 'react-apollo';
import { Auth } from 'aws-amplify';
import 'react-datepicker/dist/react-datepicker.css';

import { formatDate } from 'helpers/date';
import { ClassProp } from 'helpers/types';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import {
  useDatePicker,
  DatePickerState,
} from 'components/DatePicker/DatePicker.context';

import { ReportParams } from './components/ReportBase';
import styles from './Report.styles';
import { getUserIdentityId, getTimesliceFromPeriod } from './Report.helper';
import { useAsyncReport } from './scenes/AsyncReport';
import ReportForm from './Report.form';
import { Merchant, useMerchants } from 'hooks/useMerchants';

export type Values = {
  report: string;
  from: Date;
  to: Date;
  merchant: Merchant;
  reportList: string[];
};

export type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  location: Location;
  history: History;
  classes: Styles;
  match: match;
  whitelistReports?: string[];
};

const onClose = (history: History, location: Location) => () => {
  history.replace({ ...location, state: null });
};

const errorToastfy = (props: Props) => {
  const { location, history } = props;
  const { state } = location;

  if (state) {
    toastMapping({ onClose: onClose(history, location) }, 'error')(state);
  }
};

const renderSubmit = (
  { startDate, endDate, from, to }: DatePickerState,
  processExportReportsData: (
    options?: MutationFunctionOptions<any, OperationVariables> | undefined,
  ) => Promise<ExecutionResult<any>>,
  email: string,
  history: History,
) => async (values: Values, pathname?: string) => {
  const { merchant } = values;
  const reportList = without(['allReports'], values.reportList);

  const formattedData: ReportParams = {
    merchantName: merchant.merchantName,
    merchantId: merchant.merchantId,
    reportList,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    campaignStartDate: formatDate(startDate),
    campaignEndDate: formatDate(endDate),
    timeslice: getTimesliceFromPeriod(from, to),
  };

  if (pathname) {
    const options = {
      pathname,
      search: queryString.stringify(formattedData),
    };
    return history.push(options);
  }

  const credentials = await Auth.currentUserCredentials();
  return processExportReportsData({
    variables: {
      params: {
        ...formattedData,
        email,
        userIdentityId: getUserIdentityId(credentials),
      },
    },
  });
};

const Report: FunctionComponent<Props> = (props) => {
  const datepickerState = useDatePicker();
  const { isExternalUser } = useUserMgmt();
  const profilePayload = useQuery(GET_PROFILE);
  const processExportReportsData = useAsyncReport();
  const {
    merchants,
    merchantDropdownOptions,
    merchantsLoading,
  } = useMerchants();

  useEffect(() => {
    errorToastfy(props);
  }, []);

  const { email, ...profile } = pathOr(
    {},
    ['data', 'userProfile'],
    profilePayload,
  );

  const merchant = JSON.stringify(
    pick(['merchantId', 'merchantName'], profile),
  );

  const redirect = renderSubmit(
    datepickerState,
    processExportReportsData,
    email,
    props.history,
  );

  const { classes, match, whitelistReports } = props;

  const externalLoading = isExternalUser && merchantsLoading;
  const options = merchantDropdownOptions.options.map((option) => {
    return {
      key: option.key,
      text: option.text,
      value: option.value?.toString() ?? '',
    };
  });

  return (
    <Container className={classes.container}>
      <Header as="h3" className={classes.header}>
        Generate Report
      </Header>

      {externalLoading ? (
        <Loader active />
      ) : (
        <ReportForm
          merchants={merchants ?? []}
          loadingMerchants={merchantsLoading}
          merchantDropdownOptions={options}
          classes={classes}
          match={match}
          renderSubmit={redirect}
          merchantInitialValue={isExternalUser ? merchant : undefined}
          whitelistReports={whitelistReports}
          isExternalUser={isExternalUser}
        />
      )}
    </Container>
  );
};

export default compose<Props, any>(withStyles(styles), withRouter)(Report);
